const normalizeAccountId = accountId => {
  if (accountId === undefined || accountId === null) return null;
  if (typeof accountId === 'number') {
    return Number.isSafeInteger(accountId) && accountId > 0
      ? String(accountId)
      : null;
  }
  if (typeof accountId !== 'string') return null;

  const normalized = accountId.trim();
  return /^[1-9]\d*$/.test(normalized) ? normalized : null;
};

const isState = localState =>
  (typeof localState === 'object' && localState !== null) ||
  typeof localState === 'function';

const assertState = localState => {
  if (!isState(localState)) throw new TypeError('localState is required');
};

const requestContexts = new WeakMap();

const requestContextFor = localState => {
  assertState(localState);
  let context = requestContexts.get(localState);
  if (!context) {
    context = Object.freeze({});
    requestContexts.set(localState, context);
  }
  return context;
};

// Account-state resets must rotate an identity instead of resetting a numeric
// counter. This makes an old request fail even when an A-B-A account change
// completes entirely while that request is awaiting the network.
export const invalidateAccountRequestContext = localState => {
  assertState(localState);
  requestContexts.set(localState, Object.freeze({}));
};

export const createAccountRequestGuard = () => {
  const latestRequests = new WeakMap();

  const begin = (localState, { accountId, generation } = {}) => {
    assertState(localState);
    const previousRequest = latestRequests.get(localState);
    const request = Object.freeze({
      id: (previousRequest?.id || 0) + 1,
      accountId: normalizeAccountId(accountId),
      generation: generation ?? (previousRequest?.id || 0) + 1,
      accountRequestContext: requestContextFor(localState),
    });
    latestRequests.set(localState, request);
    return request;
  };

  const isCurrent = (localState, request, rootGetters = {}) => {
    if (
      !isState(localState) ||
      !request ||
      typeof request !== 'object' ||
      latestRequests.get(localState) !== request
    ) {
      return false;
    }
    if (requestContextFor(localState) !== request.accountRequestContext) {
      return false;
    }

    const activeAccountId = normalizeAccountId(rootGetters.getCurrentAccountId);
    return request.accountId !== null && activeAccountId === request.accountId;
  };

  return { begin, invalidate: invalidateAccountRequestContext, isCurrent };
};

// Mutation requests are independent: two deletes or updates may legitimately
// run at the same time. Unlike createAccountRequestGuard, this guard therefore
// does not apply "latest request wins" semantics. It only pins the tenant and
// the opaque account context captured before the await.
export const createAccountContextGuard = () => {
  const requestStates = new WeakMap();

  const begin = (localState, { accountId, generation } = {}) => {
    assertState(localState);
    const request = Object.freeze({
      accountId: normalizeAccountId(accountId),
      generation,
      accountRequestContext: requestContextFor(localState),
    });
    requestStates.set(request, localState);
    return request;
  };

  const isCurrent = (localState, request, rootGetters = {}) => {
    if (
      !isState(localState) ||
      !request ||
      requestStates.get(request) !== localState
    ) {
      return false;
    }
    const activeAccountId = normalizeAccountId(rootGetters.getCurrentAccountId);
    return (
      request.accountId !== null &&
      activeAccountId === request.accountId &&
      requestContextFor(localState) === request.accountRequestContext
    );
  };

  return { begin, invalidate: invalidateAccountRequestContext, isCurrent };
};

export default createAccountRequestGuard;
